import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClockIcon } from "lucide-react";
import { useState, type SyntheticEvent } from "react";

import type {
  OpsSupportLaunchResponse,
  OpsSupportSession,
  OpsSupportTargetTool,
} from "../../shared/operations-schemas";
import {
  formatSupportAccessDuration,
  SUPPORT_ACCESS_DURATION_LABEL,
  SupportAccessDurationSchema,
  type SupportAccessDuration,
  type SupportAccessRequest,
} from "../../shared/support-access-schemas";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Field, FieldError, FieldLabel } from "../components/ui/field";
import { Textarea } from "../components/ui/textarea";
import { SupportSessionCard } from "./SupportSessionCard";
import {
  listSupportAccessRequests,
  requestSupportAccess,
  startApprovedSupportSession,
} from "./support-access-api";

/**
 * The tools one approval may reach.
 *
 * Organization-wide hands back every active tool; a tool-scoped approval hands back exactly the one
 * it named. The picker then cannot offer something the launch would refuse — the server still
 * refuses it, but a control that only ever produces a 404 is a bug in the control.
 */
function toolsInScope(
  request: SupportAccessRequest,
  tools: OpsSupportTargetTool[],
): OpsSupportTargetTool[] {
  if (request.scope === "organization") return tools;
  const named = tools.find((tool) => tool.id === request.tool?.id);
  return named ? [named] : [];
}

/**
 * Asking a customer for temporary access, and using an approval once it arrives (ADR-0072).
 *
 * The employee never grants themselves anything here. Submitting the form creates a request that
 * grants nothing; the customer's administrator decides; only then does "Start session" appear, and
 * it opens a session bound to exactly what was approved.
 */

const DURATIONS = SupportAccessDurationSchema.options;

const STATUS_LABEL: Record<SupportAccessRequest["status"], string> = {
  pending: "Awaiting customer",
  approved: "Approved",
  denied: "Denied",
  expired: "Expired",
  revoked: "Ended by customer",
};

function describeScope(request: SupportAccessRequest): string {
  const mode = request.mode === "write" ? "Read and write" : "Read-only";
  return request.scope === "organization"
    ? `${mode} · whole organization`
    : `${mode} · ${request.tool?.displayName ?? "one tool"}`;
}

function RequestRow({
  request,
  organizationId,
  tools,
  session,
  onSessionStarted,
  onLaunch,
  onEnd,
  ending,
}: {
  request: SupportAccessRequest;
  organizationId: string;
  tools: OpsSupportTargetTool[];
  /** The live session opened under this approval, when there is one. */
  session: OpsSupportSession | undefined;
  onSessionStarted: () => Promise<void>;
  onLaunch: (launch: OpsSupportLaunchResponse) => void;
  onEnd: (supportSessionId: string) => void;
  ending: boolean;
}) {
  const start = useMutation({
    mutationFn: () => startApprovedSupportSession(organizationId, request.id),
    onSuccess: onSessionStarted,
  });
  // The server's answer, computed against the clock the authorization joins use, so this button is
  // never offered against an approval the next request would refuse.
  const live = request.live;
  return (
    <li className="rounded-sm border p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold">{describeScope(request)}</p>
        <Badge variant={request.status === "approved" ? "default" : "secondary"}>
          {STATUS_LABEL[request.status]}
        </Badge>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        As {request.targetUser.displayName} ({request.targetUser.username}) ·{" "}
        {request.expiresAt === null
          ? `${formatSupportAccessDuration(request.requestedDurationSeconds)} from approval`
          : `expires ${new Date(request.expiresAt * 1_000).toLocaleString()}`}
      </p>
      {live && !session ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            disabled={start.isPending}
            onClick={() => {
              start.mutate();
            }}
          >
            Start session
          </Button>
          {start.error ? <FieldError>{start.error.message}</FieldError> : null}
        </div>
      ) : null}
      {session ? (
        <div className="mt-3">
          <SupportSessionCard
            organizationId={organizationId}
            session={session}
            tools={toolsInScope(request, tools)}
            onLaunch={onLaunch}
            onEnd={onEnd}
            ending={ending}
          />
        </div>
      ) : null}
    </li>
  );
}

function ScopePicker({
  scope,
  tools,
  toolId,
  onChange,
  onToolChange,
}: {
  scope: "tool" | "organization";
  tools: OpsSupportTargetTool[];
  toolId: string;
  onChange: (scope: "tool" | "organization") => void;
  onToolChange: (toolId: string) => void;
}) {
  return (
    <Field>
      <FieldLabel htmlFor="support-access-scope">Scope</FieldLabel>
      <select
        className="h-9 w-full rounded-md border bg-background px-3 text-sm"
        id="support-access-scope"
        name="scope"
        value={scope}
        onChange={(event) => {
          onChange(event.currentTarget.value === "organization" ? "organization" : "tool");
        }}
      >
        <option value="tool">One tool</option>
        <option value="organization">The whole organization</option>
      </select>
      {scope === "tool" ? (
        <select
          className="mt-2 h-9 w-full rounded-md border bg-background px-3 text-sm"
          aria-label="Tool"
          value={toolId}
          onChange={(event) => {
            onToolChange(event.currentTarget.value);
          }}
          required
        >
          {tools.map((tool) => (
            <option key={tool.id} value={tool.id}>
              {tool.displayName}
            </option>
          ))}
        </select>
      ) : null}
    </Field>
  );
}

function TargetPicker({
  users,
  value,
  onChange,
}: {
  users: { id: string; displayName: string; username: string }[];
  value: string;
  onChange: (targetUserId: string) => void;
}) {
  return (
    <Field>
      <FieldLabel htmlFor="support-access-user">Act as</FieldLabel>
      <select
        className="h-9 w-full rounded-md border bg-background px-3 text-sm"
        id="support-access-user"
        value={value}
        onChange={(event) => {
          onChange(event.currentTarget.value);
        }}
        required
      >
        <option value="">Select a customer user</option>
        {users.map((user) => (
          <option key={user.id} value={user.id}>
            {user.displayName} ({user.username})
          </option>
        ))}
      </select>
    </Field>
  );
}

function DurationPicker({
  value,
  onChange,
}: {
  value: SupportAccessDuration;
  onChange: (duration: SupportAccessDuration) => void;
}) {
  return (
    <Field>
      <FieldLabel htmlFor="support-access-duration">Expires after</FieldLabel>
      <select
        className="h-9 w-full rounded-md border bg-background px-3 text-sm"
        id="support-access-duration"
        value={value}
        onChange={(event) => {
          onChange(SupportAccessDurationSchema.catch(value).parse(event.currentTarget.value));
        }}
      >
        {DURATIONS.map((duration) => (
          <option key={duration} value={duration}>
            {SUPPORT_ACCESS_DURATION_LABEL[duration]}
          </option>
        ))}
      </select>
    </Field>
  );
}

/**
 * Write is a separate button rather than a default, matching the direct-start form beside it.
 *
 * The labels say "Ask for", not just the mode. Two forms sit on this page and only one of them is
 * asking anybody, so a button that read "Write access" in both would be the wrong word on this one.
 */
function ModePicker({
  mode,
  canWrite,
  onChange,
}: {
  mode: "read" | "write";
  canWrite: boolean;
  onChange: (mode: "read" | "write") => void;
}) {
  return (
    <div className="flex gap-2">
      <Button
        type="button"
        variant={mode === "read" ? "default" : "outline"}
        onClick={() => {
          onChange("read");
        }}
      >
        Ask for read-only
      </Button>
      {canWrite ? (
        <Button
          type="button"
          variant={mode === "write" ? "destructive" : "outline"}
          onClick={() => {
            onChange("write");
          }}
        >
          Ask for write access
        </Button>
      ) : null}
    </div>
  );
}

function RequestForm({
  organizationId,
  canWrite,
  users,
  tools,
  onSubmitted,
}: {
  organizationId: string;
  canWrite: boolean;
  users: { id: string; displayName: string; username: string }[];
  tools: OpsSupportTargetTool[];
  onSubmitted: () => Promise<void>;
}) {
  const [scope, setScope] = useState<"tool" | "organization">("tool");
  const [mode, setMode] = useState<"read" | "write">("read");
  const [targetUserId, setTargetUserId] = useState("");
  const [toolId, setToolId] = useState("");
  const [reason, setReason] = useState("");
  const [duration, setDuration] = useState<SupportAccessDuration>("1h");
  const submit = useMutation({
    mutationFn: (input: Parameters<typeof requestSupportAccess>[1]) =>
      requestSupportAccess(organizationId, input),
    onSuccess: onSubmitted,
  });
  // Controlled rather than read back off the DOM through `FormData`. Everything submitted here is
  // one of a fixed set, and holding those in state means the request built below is the state the
  // screen is showing rather than a re-parse of it.
  const selectedTool = scope === "tool" ? toolId || (tools[0]?.id ?? "") : "";
  function onSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    // The browser's own `required` normally prevents this; a scripted submit bypasses it, and a
    // request naming nobody would ask a customer to approve a blank.
    if (!targetUserId) return;
    submit.mutate({
      targetUserId,
      reason,
      scope,
      ...(selectedTool ? { toolId: selectedTool } : {}),
      mode,
      duration,
      confirmWrite: mode === "write",
    });
  }
  return (
    <form className="grid gap-3" onSubmit={onSubmit}>
      <Field>
        <FieldLabel htmlFor="support-access-reason">Reason for the customer</FieldLabel>
        <Textarea
          id="support-access-reason"
          value={reason}
          onChange={(event) => {
            setReason(event.currentTarget.value);
          }}
          minLength={10}
          maxLength={500}
          required
        />
      </Field>
      <TargetPicker users={users} value={targetUserId} onChange={setTargetUserId} />
      <ScopePicker
        scope={scope}
        tools={tools}
        toolId={selectedTool}
        onChange={setScope}
        onToolChange={setToolId}
      />
      <DurationPicker value={duration} onChange={setDuration} />
      <ModePicker mode={mode} canWrite={canWrite} onChange={setMode} />
      {submit.error ? <FieldError>{submit.error.message}</FieldError> : null}
      <Button
        className="w-fit"
        type="submit"
        disabled={
          submit.isPending || users.length === 0 || (scope === "tool" && tools.length === 0)
        }
      >
        Ask the customer for access
      </Button>
    </form>
  );
}

export function SupportAccessRequests({
  organizationId,
  canStart,
  canWrite,
  users,
  tools,
  sessions,
  onSessionStarted,
  onLaunch,
  onEnd,
  ending,
}: {
  organizationId: string;
  canStart: boolean;
  canWrite: boolean;
  users: { id: string; displayName: string; username: string }[];
  tools: OpsSupportTargetTool[];
  /** Live sessions opened under an approval, already filtered to exclude the unilateral path. */
  sessions: OpsSupportSession[];
  onSessionStarted: () => Promise<void>;
  onLaunch: (launch: OpsSupportLaunchResponse) => void;
  onEnd: (supportSessionId: string) => void;
  ending: boolean;
}) {
  const queryClient = useQueryClient();
  const requests = useQuery({
    queryKey: ["ops", "support-access", organizationId],
    queryFn: () => listSupportAccessRequests(organizationId),
    enabled: canStart,
  });
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["ops", "support-access", organizationId] });
  };
  if (!canStart) return null;
  const rows = requests.data?.requests ?? [];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Request customer approval</CardTitle>
        <CardDescription>
          A request grants nothing on its own. An administrator at the customer approves or denies
          it, the approval covers only what was asked for, and it ends by itself at the time you
          requested. They can also end it early.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows.length > 0 ? (
          <ul className="grid gap-2">
            {rows.map((request) => (
              <RequestRow
                key={request.id}
                request={request}
                organizationId={organizationId}
                tools={tools}
                session={sessions.find((session) => session.accessRequestId === request.id)}
                onSessionStarted={async () => {
                  await Promise.all([refresh(), onSessionStarted()]);
                }}
                onLaunch={onLaunch}
                onEnd={onEnd}
                ending={ending}
              />
            ))}
          </ul>
        ) : (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <ClockIcon className="size-4" aria-hidden="true" /> No requests against this customer
            yet.
          </p>
        )}
        <RequestForm
          organizationId={organizationId}
          canWrite={canWrite}
          users={users}
          tools={tools}
          onSubmitted={refresh}
        />
      </CardContent>
    </Card>
  );
}
